

import { Box, Button, Typography, useTheme } from '@mui/material'
import PropTypes from 'prop-types'
import React from 'react'
import CountBadge from './CountBadge'

/**
 * CustomTabs
 *
 * A lightweight, theme-aware tab control built with MUI components.
 * Renders a horizontal set of buttons representing tabs. Two layout variants are supported:
 * - "aligned-left" (default): compact tabs with rounded selected state
 * - "stretched": tabs stretch to fill the container and show a bottom-border indicator for the active tab
 *
 * @typedef {Object} Tab
 * @property {string|number} value - Unique identifier/value for the tab.
 * @property {import('react').ReactNode} label - Visible label for the tab (string, node, etc.).
 * @property {import('react').ReactNode} [icon] - Optional icon node to render before the label.
 *
 * @param {Object} props - Component props
 * @param {string|number} props.value - Currently selected tab value. Determines active styling.
 * @param {(newValue: string|number) => void} props.handleTabChange - Callback invoked when a tab is clicked. Receives the clicked tab's value.
 * @param {Tab[]} props.tabs - Array of tab definitions. Each entry must include `value` and `label`, and may include `icon`.
 * @param {import('@mui/system').SxProps} [props.styles={}] - Optional sx styles applied to the root Box container.
 * @param {'aligned-left'|'stretched'} [props.variant='aligned-left'] - Visual variant to use. "stretched" expands tabs to fill the width; "aligned-left" shows compact, raised tabs.
 *
 * @returns {import('react').ReactElement} The rendered tabs element.
 *
 * Usage example:
 * const tabs = [
 *   { value: 'all', label: 'All', icon: <AllIcon /> },
 *   { value: 'open', label: 'Open' },
 * ];
 * <CustomTabs value={value} handleTabChange={setValue} tabs={tabs} variant="stretched" />
 *
 */
const CustomTabs = ( { value, handleTabChange, tabs, styles = {}, variant = 'aligned-left' } ) => {

    const { palette, shadows, border } = useTheme()

    if ( variant === 'stretched' ) {
        return (
            <Box bgcolor={palette.contentBg} overflow="clip" borderBottom={border[1]} flexWrap="wrap" display="flex" sx={styles}>
                {tabs.map( tab => (
                    <Button
                        disableTouchRipple
                        key={tab.value}
                        onClick={() => handleTabChange( tab.value )}
                        variant="text"
                        disableElevation
                        sx={{
                            gap: "5px",
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            borderRadius: 0,
                            padding: '5px 10px',
                            textTransform: 'none',
                            color: value === tab.value ? palette.text.primary : palette.text.disabled,
                            borderBottom: value === tab.value
                                ? `2px solid ${palette.text.primary}`
                                : `2px solid transparent`,
                            backgroundColor: value === tab.value ? palette.action.selected : "transparent",
                            '&:hover': {
                                color: value !== tab.value && palette.text.secondary,
                                backgroundColor: value === tab.value ? palette.action.selected : palette.action.hover,
                            },
                        }}
                    >
                        {tab.icon ? <Box display="flex" height="100%" alignItems="center" component="span" fontSize="1.2em" >{tab.icon}</Box> : ""}
                        {tab.label}
                        {parseInt( tab.count ) >= 0 && <CountBadge value={tab.count} />}
                    </Button>
                ) )}
            </Box>
        )
    } else
        return (
            <Box width="fit-content" borderRadius="5px" boxShadow={`inset ${shadows[1]}`} bgcolor={palette.contentBg} display="flex" gap="10px" padding="5px" sx={styles}>
                {tabs.map( tab => (
                    <Button
                        disableTouchRipple
                        key={tab.value}
                        onClick={() => handleTabChange( tab.value )}
                        variant="text"
                        disableElevation
                        sx={{
                            gap: "5px",
                            minWidth: '100px',
                            maxWidth: '400px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: '3px 10px',
                            textTransform: 'none',
                            borderRadius: '3px',
                            boxShadow: value === tab.value ? shadows[1] : 'none',
                            color: value === tab.value ? palette.common.font : palette.text.disabled,
                            bgcolor: value === tab.value ? palette.background.paper : palette.common.default,
                            '&:hover': {
                                color: value !== tab.value && palette.text.secondary,
                                background: value === tab.value ? palette.background.paper : "none",
                                boxShadow: value === tab.value ? shadows[1] : 'none',
                            },
                        }}
                    >
                        {tab.icon ? <Box display="flex" height="100%" alignItems="center" component="span" fontSize="1.2em" >{tab.icon}</Box> : ""}
                        {tab.label}
                        {parseInt( tab.count ) >= 0 && <CountBadge value={tab.count} />}
                    </Button>
                ) )}
            </Box>
        )
}

export default CustomTabs


CustomTabs.propTypes = {
    value: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ).isRequired,
    handleTabChange: PropTypes.func.isRequired,
    tabs: PropTypes.arrayOf( PropTypes.shape( {
        value: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ).isRequired,
        label: PropTypes.node.isRequired,
        icon: PropTypes.node,
    } ) ).isRequired,
    styles: PropTypes.object,
    variant: PropTypes.oneOf( ['aligned-left', 'stretched', 'switch'] ),
}